import React from 'react';
import { Formik, Form } from 'formik';
import _isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';
import { Prompt } from 'react-router';
import { combineStatuses, useStatus } from 'resift';
import * as Types from 'pages/Pages/typings';
import makeUpdateEntityType from 'fetches/makeUpdateEntityType';
import makeEntityTypesFetch from 'store/configuration/actions/entityTypesFetch';

import PageFormToolbar from 'components/Page/newComponents/PageFormToolbar';
import PageFormErrorMessage from 'components/Page/newComponents/PageFormErrorMessage';
import SaveButton from 'components/Page/SaveButton';
import CancelButton from 'components/Page/CancelButton';
import HandleRedirect from 'molecules/HandleRedirect';
import PageTypeName from './PageTypeName';
import PageEditors from './PageEditors';
import SearchSettings from './SearchSettings';
import DeletePages from './DeletePages';
import Loader from 'components/Loader';

import { LayoutObject } from 'pages/AttributesEdit/AttributesEdit';

interface Props {
  entityType: Types.EntityType;
  isIndependentPage: boolean;
  onUpdateEntityType: (data: Types.UpdateEntity) => void;
  config: any;
  onUpdateLayout: (payload: LayoutObject[]) => void;
}

function SettingsTab({
  entityType,
  isIndependentPage,
  onUpdateEntityType,
  config,
  onUpdateLayout,
}: Props) {
  // update entity type:
  const updateEntityType = makeUpdateEntityType(_get(entityType, 'id'));
  const updateEntityTypeStatus = useStatus(updateEntityType) || 1;

  // entity types
  const entityTypesFetch = makeEntityTypesFetch();
  const entityTypesStatus = useStatus(entityTypesFetch) || 1;

  const combinedStatus = combineStatuses(entityTypesStatus, updateEntityTypeStatus);

  const userStatus = useStatus(entityTypesFetch);

  const initialState: Types.EntitySettingsFormValues = {
    name: entityType.name,
    description: entityType.description,
    settings: {
      implicitEditorRule: entityType._settings.implicitEditorRule,
      directorySearch: {
        enabled: entityType._settings.directorySearch.enabled,
        displayOnEmptySearch: entityType._settings.directorySearch.displayOnEmptySearch,
      },
    },
    layouts: {
      filters: _get(config, 'layouts.filters', []),
    },
  };

  const handleValidation = (values: Types.EntitySettingsFormValues) => {
    let errors: Types.EntitySettingsFormErrors = {};
    if (_get(values, 'name') === '') {
      errors.name = 'Name is a required field.';
    }

    return errors;
  };

  const handleSubmit = (values: Types.EntitySettingsFormValues) => {
    onUpdateEntityType(values);
    const layoutPayloadKeys = Object.keys(values.layouts).filter(
      // @ts-ignore
      key => !_isEqual(values.layouts[key], initialState.layouts[key]),
    );
    const layoutPayload = layoutPayloadKeys.map(key => ({
      entityField: entityType.id,
      layoutKey: key,
      // @ts-ignore
      children: values.layouts[key],
    }));

    layoutPayload && onUpdateLayout(layoutPayload);
  };

  return (
    <Loader status={combinedStatus}>
      {() => (
        <Formik
          initialValues={initialState}
          enableReinitialize
          onSubmit={handleSubmit}
          validate={handleValidation}
        >
          {props => {
            const { values, setFieldValue, dirty, resetForm } = props;
            const errors: Types.EntitySettingsFormErrors = props.errors;

            return (
              <Form>
                <PageTypeName values={values} entityType={entityType} onChange={setFieldValue} />
                <PageEditors
                  isIndependentPage={isIndependentPage}
                  values={values}
                  onChange={setFieldValue}
                />
                <SearchSettings
                  setFieldValue={setFieldValue}
                  values={values}
                  errors={errors}
                  userStatus={userStatus}
                  fields={config.fields}
                  onChange={setFieldValue}
                />
                <DeletePages entityType={entityType} />
                <PageFormToolbar
                  dirty={dirty || !_isEmpty(errors)}
                  preContent={<PageFormErrorMessage errors={errors} />}
                >
                  <CancelButton
                    data-testid="membership-cancel"
                    onClick={resetForm}
                    disabled={!_isEmpty(errors)}
                  />
                  <SaveButton
                    label="Save"
                    onClick={() => {
                      props.handleSubmit();
                      resetForm({ values });
                    }}
                    disabled={!dirty || !_isEmpty(errors)}
                  />
                </PageFormToolbar>
                <Prompt
                  when={dirty || !_isEmpty(errors)}
                  message="Your work is not saved, or you have errors that need to be addressed! Are you sure you want to leave?"
                />
                <HandleRedirect />
              </Form>
            );
          }}
        </Formik>
      )}
    </Loader>
  );
}

export default SettingsTab;
